import { useState, useEffect } from "react";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import { blogEndpoints } from "../../services/api";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router";
import { motion } from "framer-motion";
const { CREATE_BLOG, UPLOAD_IMAGE } = blogEndpoints;

export default function BlogForm() {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: "",
    author: "Admin",
    thumbnail: null,
  });

  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const { quill, quillRef } = useQuill();

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle thumbnail upload
  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, thumbnail: file }));

    // Preview the image
    const reader = new FileReader();
    reader.onloadend = () => setThumbnailPreview(reader.result);
    reader.readAsDataURL(file);
  };

  // Resize image before uploading
  const resizeImage = (file, width = 800, height = null) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const aspectRatio = img.width / img.height;

          if (!height) {
            height = Math.round(width / aspectRatio);
          }

          canvas.width = width;
          canvas.height = height;

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() }));
          }, "image/jpeg", 0.8);
        };

        img.onerror = () => reject(new Error("Error loading image"));
      };

      reader.onerror = () => reject(new Error("Error reading file"));
    });
  };

  // Insert image into the Quill editor
  const insertToEditor = (url, width, height) => {
    if (!quill) {
      console.error("Quill editor is not initialized.");
      return;
    }
  
    const range = quill.getSelection() || { index: quill.getLength() };
  
    quill.insertEmbed(range.index, "image", url);
    toast.success("Image inserted successfully!");
  };
  
  

  // Upload image to the server
  const saveToServer = async (file, width, height) => {
    toast.loading("Resizing image...");
    
    try {
      const resizedFile = await resizeImage(file, width, height);
      toast.dismiss();
      toast.loading("Uploading image...");

      const body = new FormData();
      body.append("image", resizedFile);
      const token = localStorage.getItem("token"); 

      const res = await fetch(UPLOAD_IMAGE, {
        method: "POST",
        body, 
        headers: {
            Authorization: `Bearer ${token}`, 
        },
        credentials: "include", 
    });
      const data = await res.json();
      console.log("data : ", data)

      toast.dismiss();

      if (data.url) {
        insertToEditor(data.url, width, height);
      } else {
        console.error("❌ Error: Image URL not found in response", data);
        toast.error("Failed to upload image.");
      }
    } catch (error) {
      console.error("❌ Error processing image:", error);
      toast.dismiss();
      toast.error("Image processing failed!");
    }
  };

  
  const selectLocalImage = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = () => {
      if (input.files && input.files[0]) {
        const file = input.files[0];

        let width = window.prompt("Enter image width (default 800px):", "800");
        let height = window.prompt("Enter image height (leave blank for auto):", "");

        width = parseInt(width) || 800;
        height = height ? parseInt(height) : null;

        saveToServer(file, width, height);
      } else {
        console.error("❌ No file selected.");
      }
    };
  };

  const removeImageFromEditor = () => {
    if (!quill) {
      toast.error("Quill editor is not initialized.");
      return;
    }
  
    const images = quill.root.querySelectorAll("img");
  
    if (images.length > 0) {
      const lastImage = images[images.length - 1]; 
      lastImage.parentNode.removeChild(lastImage); 
      toast.success("Last inserted image removed!");
    } else {
      toast.error("No image found in the editor.");
    }
  };

  const [loading,setLoading] = useState(false);
  
  

  useEffect(() => {
    if (quill) {
      const toolbar = quill.getModule("toolbar");

      if (toolbar) {
        toolbar.addHandler("image", selectLocalImage);
      }

      quill.on("text-change", () => {
        setFormData((prev) => ({ ...prev, content: quill.root.innerHTML }));
      });
    }
  }, [quill]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.loading("Publishing blog...");

    // Creating FormData object
    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("content", formData.content);
    formDataToSend.append("author", formData.author);
    
    // Append thumbnail only if it's selected
    if (formData.thumbnail) {
        formDataToSend.append("thumbnail", formData.thumbnail);
    }

    try {
      const token = localStorage.getItem("token"); 
      setLoading(true);

      const res = await fetch(CREATE_BLOG, {
          method: "POST",
          body: formDataToSend, 
          headers: {
              Authorization: `Bearer ${token}`, 
          },
          credentials: "include", 
      });
      

        const data = await res.json();
        console.log("Response:", data);
        setLoading(false)
        toast.dismiss();

        if (res.ok) {
            toast.success("🎉 Blog published successfully!");
        } else {
            toast.error("❌ Failed to publish blog.");
        }
    } catch (error) {
        console.error("Error submitting form:", error);
        toast.dismiss();
        toast.error("❌ Server error!");
    }
};


  const navigate = useNavigate()

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#0F172A] px-6 py-12">
    <Toaster />

    {/* Blog Form Card */}
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-3xl bg-white p-8 rounded-lg shadow-2xl"
    >
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-[#0F172A] text-center md:text-left">
          Create a Blog Post
        </h2>
        <motion.button
          onClick={() => navigate("/admin/blog/getallblog")}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
          className="mt-4 md:mt-0 py-2 px-4 bg-[#C9A227] text-[#0F172A] font-semibold rounded-lg shadow-md hover:bg-[#B79527] transition"
        >
          See Blogs
        </motion.button>
      </div>

      {/* Blog Title */}
      <label className="text-[#0F172A] font-medium">Title</label>
      <input
        type="text"
        name="title"
        placeholder="Enter Blog Title"
        value={formData.title}
        onChange={handleChange}
        required
        className="w-full p-3 mb-4 bg-gray-100 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A227] outline-none"
      />

      {/* Blog Description */}
      <label className="text-[#0F172A] font-medium">Description</label>
      <textarea
        name="description"
        placeholder="Enter Blog Description"
        value={formData.description}
        onChange={handleChange}
        required
        className="w-full p-3 mb-4 bg-gray-100 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C9A227] outline-none"
        rows="3"
      />

      {/* Thumbnail Upload */}
      <label className="text-[#0F172A] font-medium">Upload Thumbnail</label>
      <input type="file" accept="image/*" onChange={handleThumbnailUpload} className="mb-4 block text-gray-700" />
      {thumbnailPreview && (
        <img src={thumbnailPreview} alt="Thumbnail Preview" className="w-full max-h-48 object-cover rounded-lg mb-4" />
      )}

      {/* QuillJS Rich Text Editor */}
      <label className="text-[#0F172A] font-medium">Content</label>
      <div className="w-full mb-4 bg-gray-100 border border-gray-300 rounded-lg">
        <div ref={quillRef} className="w-full min-h-[300px]" />
      </div>

      {/* Remove Image Button */}
      <motion.button
        type="button"
        onClick={removeImageFromEditor}
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
        className="w-full py-2 bg-red-600 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 transition mb-4"
      >
        Remove Last Image
      </motion.button>

      {/* Publish Button */}
      <motion.button
        type="submit"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
        className={`w-full py-3 bg-[#C9A227] text-[#0F172A] font-semibold rounded-lg shadow-md ${
          loading && "cursor-not-allowed bg-gray-500"
        } hover:bg-[#B79527] transition`}
      >
        {loading ? "Publishing..." : "Publish"}
      </motion.button>
    </motion.form>
  </div>
  );
}
